import { useTranslation } from "react-i18next"
import { Button } from "@gogoro/go-design"
import { ChevronRight } from "@styled-icons/feather/ChevronRight"
import { useAuth } from "customerAttachments/auth/AuthProvider"
import Card from "customerAttachments/components/Card"
import Loading from "customerAttachments/components/Loading"
import { PurchaseOrderStatusMapping, TransferOrderStatusMapping } from "customerAttachments/constants"
import useWindowDimensions, { DeviceType } from "customerAttachments/hooks/useWIndowDimensions"
import { OrderPurchaseData, OrderTransferData, OrderType } from "customerAttachments/types"
import { useNavigation } from "modules/navigation"

import { useGetPurchaseOrders, useGetTransferOrders } from "./hooks/useGetOrders"

import styles from "./styles.module.scss"

type ListItemProps = {
  orderNo: string
  text: string
  goNext: () => void
}

const ListItem = ({ orderNo, text, goNext }: ListItemProps) => {
  return (
    <div className={styles.listContent} onClick={goNext} data-testid={orderNo}>
      <h4>{orderNo}</h4>
      <div>
        <span>{text}</span>
        <ChevronRight size={24} />
      </div>
    </div>
  )
}

type OrderListProps = {
  title: string
  amount: number
  data: (OrderTransferData | OrderPurchaseData)[]
  type: OrderType
}

const OrdersList = ({ title, amount, data, type }: OrderListProps) => {
  const { navigateTo } = useNavigation()
  const { t } = useTranslation("sales")

  const renderListItems = (data: (OrderPurchaseData | OrderTransferData)[], type: OrderType) =>
    data.map((item) => {
      const orderNo = "OrderNo" in item ? item.OrderNo : item.TransferId
      const statusMapping = type === "purchase" ? PurchaseOrderStatusMapping : TransferOrderStatusMapping
      const status = statusMapping[item.Status as keyof typeof statusMapping]
      const goNext = () => navigateTo(`/${type}/${orderNo}`)
      return <ListItem key={orderNo} orderNo={orderNo} text={status} goNext={goNext} />
    })

  if (!data.length) return null

  return (
    <Card
      title={
        <>
          <div>{title}</div>
          <span>
            ({t("order.detail_col_product_qty")} : {amount})
          </span>
        </>
      }
      content={renderListItems(data, type)}
      titleClass={styles.orderList_listTitle}
    />
  )
}

const OrderListPage = () => {
  const { data: transferData, isLoading: isTransferLoading } = useGetTransferOrders()
  const { data: purchaseData, isLoading: isPurchaseLoading } = useGetPurchaseOrders()
  const transferOrders = transferData?.Transfers ?? []
  const purchaseOrders = purchaseData?.Orders ?? []
  const isDataLoading = isTransferLoading || isPurchaseLoading
  const { user } = useAuth() || {}
  const { t } = useTranslation(["sales", "label"])
  const { height, deviceType } = useWindowDimensions()
  const isMobile = deviceType === DeviceType.MOBILE

  const renderOrderListContent = () => {
    if (purchaseOrders.length || transferOrders.length) {
      return (
        <>
          <OrdersList
            title={t("customer_attachments.purchase_order")}
            amount={purchaseOrders.length}
            data={purchaseOrders}
            type="purchase"
          />
          <OrdersList
            title={t("customer_attachments.transfer_order")}
            amount={transferOrders.length}
            data={transferOrders}
            type="transfer"
          />
        </>
      )
    } else {
      return (
        <Card
          title={t("customer_attachments.purchase_order")}
          content={
            <div className={styles.emptyOrder}>
              <h3>{t("customer_attachments.no_active_orders")}</h3>
              <p>{t("customer_attachments.welcome_text")}</p>
              <Button onClick={() => window.open(`https://www.gogoro.com/tw/explore-my-ride`)}>
                {t("customer_attachments.explore_your_ride")}
              </Button>
            </div>
          }
        />
      )
    }
  }

  return (
    <div className={styles.orderList_container}>
      <Card
        title={t("label:description")}
        content={
          <div className={styles.orderList_description}>
            {t("customer_attachments.welcome_description", { name: user?.Name || "" })}
          </div>
        }
      />
      <div className={styles.orderList_list} style={{ height: `calc(${height}px - ${isMobile ? 221 : 245}px)` }}>
        <Loading loading={isDataLoading}>{renderOrderListContent()}</Loading>
      </div>
    </div>
  )
}

export default OrderListPage
